import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import TagReviewModal from '../candidates/browse/TagReviewModal';
import GeneralistCard from '../../components/candidates/generalist_card';

export default function InternalRecommendations() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeRecommendations, setActiveRecommendations] = useState({});
  const [activeTabRoleId, setActiveTabRoleId] = useState(null);
  const [processedResults, setProcessedResults] = useState({});
  const [error, setError] = useState(null);

  // New state for advanced filtering and candidate selection
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedCandidates, setSelectedCandidates] = useState({});
  const [emailTemplate, setEmailTemplate] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);

  // New state for filters popup and editable email
  const [showFiltersPopup, setShowFiltersPopup] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [editableEmailTemplate, setEditableEmailTemplate] = useState('');

  // New state for team member selection
  const [selectedTeamMember, setSelectedTeamMember] = useState('martha');

  // New state for TagReviewModal integration
  const [showTagReviewModal, setShowTagReviewModal] = useState(false);
  const [currentJDAnalysis, setCurrentJDAnalysis] = useState(null);
  const [selectedRoleForReview, setSelectedRoleForReview] = useState(null);
  const [isLoadingJDAnalysis, setIsLoadingJDAnalysis] = useState(false);
  
  // State for tracking current search criteria (for edit functionality)
  const [currentSearchCriteria, setCurrentSearchCriteria] = useState({
    jdAnalysis: null,
    gender: "any",
    selectedStream: "",
    requireNoVisa: false,
    role: null
  });

  // New state for email template view mode
  const [emailViewMode, setEmailViewMode] = useState('preview'); // 'preview' or 'source'

  // New state for template selection (email or whatsapp)
  const [activeTemplate, setActiveTemplate] = useState('email'); // 'email' or 'whatsapp'

  // New state for remaining candidates section
  const [showRemainingCandidates, setShowRemainingCandidates] = useState(false);

  // New state for shortlist activity data
  const [shortlistActivity, setShortlistActivity] = useState({}); // Role ID -> latest activity

  // New state for collapsible templates section
  const [showTemplates, setShowTemplates] = useState(false);

  // Ref for scrolling to templates section
  const templatesRef = useRef(null);

// New state for recent recommendations
const [recentRecommendations, setRecentRecommendations] = useState([]);
const [isLoadingRecentRecommendations, setIsLoadingRecentRecommendations] = useState(false);
const [recentRecommendationsError, setRecentRecommendationsError] = useState(null);
const [isRecentCollapsed, setIsRecentCollapsed] = useState(false);
  
  // New state for loading saved recommendations
  const [isLoadingSavedRecommendations, setIsLoadingSavedRecommendations] = useState(false);

  // Function to handle create template - expand and scroll
  const handleCreateTemplate = () => {
    setShowTemplates(true);
    // Small delay to ensure the component has expanded before scrolling
    setTimeout(() => {
      if (templatesRef.current) {
        templatesRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  // Team member options
  const teamMembers = [
    { id: 'martha', name: 'Martha' },
    { id: 'darcy', name: 'Darcy' },
    { id: 'matthew', name: 'Matthew' },
    { id: 'bethan', name: 'Bethan' },
    { id: 'rachel', name: 'Rachel' },
    { id: 'jared', name: 'Jared' }
  ];

  // Enhanced role filtering and sorting
  const filteredAndSortedRoles = useMemo(() => {
    let filtered = roles;

    // Apply search filter
    if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(role => {
      const roleTitle = (role["Role Title"] || "").toLowerCase();
      const companyName = Array.isArray(role["Name (from Startup)"]) 
        ? (role["Name (from Startup)"][0] || "").toLowerCase()
        : (role["Company"] || "").toLowerCase();
      
        return roleTitle.includes(query) || companyName.includes(query);
      });
    }

    // Apply role status and team member filters (AND logic - all selected filters must match)
    if (selectedFilters.length > 0) {
      filtered = filtered.filter(role => {
        const roleDropStatus = role["Role Drop Status (list visibility)"] || "";
        const newRoleStageTag = role["New role stage tag (Attio)"] || "";
        const appsThisMonth = parseInt(role["# Apps this month"] || "0");
        const roleOwner = role["Role Owner"] || "";

        // Check if ALL selected filters match (AND logic)
        return selectedFilters.every(filter => {
          switch (filter) {
            case 'live':
              return roleDropStatus === 'Live';
            case 'live_last_week':
              return roleDropStatus === 'Live last week';
            case 'preview':
              return roleDropStatus === 'Preview';
            case 'in_process':
              return newRoleStageTag === 'In-Process';
            case 'live_low_apps':
              return roleDropStatus === 'Live' && appsThisMonth < 10;
            case 'live_last_week_low_apps':
              return roleDropStatus === 'Live last week' && appsThisMonth < 10;
            // Team member filters
            case 'owner_martha':
              return roleOwner === 'Martha';
            case 'owner_darcy':
              return roleOwner === 'Darcy';
            case 'owner_matthew':
              return roleOwner === 'Matthew';
            case 'owner_bethan':
              return roleOwner === 'Bethan';
            case 'owner_rachel':
              return roleOwner === 'Rachel';
            case 'owner_jared':
              return roleOwner === 'Jared';
            default:
              return false;
          }
        });
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const dateA = new Date(a["Role created (Attio)"] || 0);
      const dateB = new Date(b["Role created (Attio)"] || 0);
      const appsA = parseInt(a["# Apps this month"] || "0");
      const appsB = parseInt(b["# Apps this month"] || "0");

      switch (sortBy) {
        case 'newest':
          return dateB - dateA;
        case 'oldest':
          return dateA - dateB;
        case 'most_apps':
          return appsB - appsA;
        case 'least_apps':
          return appsA - appsB;
        default:
          return dateB - dateA;
      }
    });

    return filtered;
  }, [roles, searchQuery, selectedFilters, sortBy]);

  // Calculate counts for each filter category
  const filterCounts = useMemo(() => {
    const counts = {
      all: roles.length,
      live: 0,
      live_last_week: 0,
      preview: 0,
      in_process: 0,
      live_low_apps: 0,
      live_last_week_low_apps: 0,
      owner_martha: 0,
      owner_darcy: 0,
      owner_matthew: 0,
      owner_bethan: 0,
      owner_rachel: 0,
      owner_jared: 0
    };

    roles.forEach(role => {
      const roleDropStatus = role["Role Drop Status (list visibility)"] || "";
      const newRoleStageTag = role["New role stage tag (Attio)"] || "";
      const appsThisMonth = parseInt(role["# Apps this month"] || "0");
      const roleOwner = role["Role Owner"] || "";

      if (roleDropStatus === 'Live') {
        counts.live++;
        if (appsThisMonth < 10) {
          counts.live_low_apps++;
        }
      }
      if (roleDropStatus === 'Live last week') {
        counts.live_last_week++;
        if (appsThisMonth < 10) {
          counts.live_last_week_low_apps++;
        }
      }
      if (roleDropStatus === 'Preview') {
        counts.preview++;
      }
      if (newRoleStageTag === 'In-Process') {
        counts.in_process++;
      }

      // Count team member assignments
      switch (roleOwner) {
        case 'Martha':
          counts.owner_martha++;
          break;
        case 'Darcy':
          counts.owner_darcy++;
          break;
        case 'Matthew':
          counts.owner_matthew++;
          break;
        case 'Bethan':
          counts.owner_bethan++;
          break;
        case 'Rachel':
          counts.owner_rachel++;
          break;
        case 'Jared':
          counts.owner_jared++;
          break;
      }
    });

    return counts;
  }, [roles]);

  // Add helper function to extract names from reason text
  const extractNameFromReason = (reason) => {
    if (!reason) return null;
    
    // Look for common patterns where names appear at the beginning of the sentence
    const namePatterns = [
      /^([A-Z][a-z]+(?:\s[A-Z][a-z]+)?)\s(?:has|is|was|worked|led|demonstrated)/,  // "Emma has", "John Smith is"
      /^([A-Z][a-z]+(?:\s[A-Z][a-z]+)?)\s(?:brings|shows|possesses|offers|graduated)/,  // "Emma brings", "John Smith shows"
      /^([A-Z][a-z]+(?:\s[A-Z][a-z]+)?)'s/  // "Emma's", "John Smith's"
    ];
    
    for (const pattern of namePatterns) {
      const match = reason.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  };

  // Generate plain text email template (for copying to email composers)
  const generatePlainTextEmailTemplate = (roleData) => {
    const selectedCandidateIds = Object.keys(selectedCandidates[roleData.roleId] || {})
      .filter(id => selectedCandidates[roleData.roleId][id]);
    
    const selectedCandidateData = roleData.candidates.filter(c => 
      selectedCandidateIds.includes(c.id)
    );

    const companyName = roleData.roleDetails?.companyName || "the team";
    const roleTitle = roleData.roleTitle;
    const contactName = roleData.roleDetails?.contactName || "there";
    const teamMemberName = teamMembers.find(member => member.id === selectedTeamMember)?.name || 'Team';
    
    // Get the shortlist link from the role data
    const role = roles.find(r => r.id === roleData.roleId);
    // Always use a clean shortlist link: https://app.jumpstart-uk.com/shortlists or https://app.jumpstart-uk.com/shortlists?roleId=...
    let shortlistLink = "https://app.jumpstart-uk.com/shortlists";
    if (role && (role["URL ID"] || role["url_id"])) {
      const urlId = role["URL ID"] || role["url_id"];
      shortlistLink = `https://app.jumpstart-uk.com/shortlists?roleId=${urlId}`;
    }

    // Helper function to construct candidate URL with login details
    const getCandidateUrlWithLogin = (candidateUrlId) => {
      // Always return the clean candidate profile URL with no credentials or page params
      return `https://app.jumpstart-uk.com/candidate/${candidateUrlId}`;
    };

    if (selectedCandidateData.length === 0) {
      return `Hey ${contactName},\n\nI've created a shortlist of strong candidates (${shortlistLink}) that I think would be a great fit for the role.\n\nPlease select candidates to include in this email template.\n\nBest,\n${teamMemberName}`;
    }

    let template = `Hey ${contactName},\n\n`;
    template += `I've created a shortlist of strong candidates (${shortlistLink}) that I think would be a great fit for the role.\n\n`;
    template += `**Check out the top ${selectedCandidateData.length} here:**\n\n`;

    selectedCandidateData.forEach((candidate, index) => {
      // Use actual candidate name if available, fallback to extracting from reason, then fallback to generic name
      const actualName = candidate.name || candidate.Name || candidate["Name"] || candidate["Candidate Name"];
      const extractedName = extractNameFromReason(candidate.reason);
      const displayName = actualName || extractedName || `Candidate ${index + 1}`;
      
      // Get the AI rationale - try multiple possible fields
      const aiReason = candidate.reason || candidate.aiReason || candidate.AIRationale || candidate.aiRationale || 'Strong technical background and relevant experience.';
      
      // Get candidate profile URL using url_id or fallback to id
      const candidateUrlId = candidate.url_id || candidate.id;
      const candidateProfileUrl = getCandidateUrlWithLogin(candidateUrlId);
      
      console.log(`PlainText - Candidate ${displayName} - URL ID:`, candidateUrlId, 'Full candidate:', { id: candidate.id, url_id: candidate.url_id }); // Debug URL ID
      
      template += `• ${displayName}: ${aiReason}\n`;
    });

    template += `\nCheck out their full profiles along with a few others here. **Want to talk to any of them?**\n\n`;
    template += `Best,\n${teamMemberName}`;

    return template;
  };

  // Generate WhatsApp template
  const generateWhatsAppTemplate = (roleData) => {
    const selectedCandidateIds = Object.keys(selectedCandidates[roleData.roleId] || {})
      .filter(id => selectedCandidates[roleData.roleId][id]);
    
    const selectedCandidateData = roleData.candidates.filter(c => 
      selectedCandidateIds.includes(c.id)
    );

    const companyName = roleData.roleDetails?.companyName || "the team";
    const roleTitle = roleData.roleTitle;
    const contactName = roleData.roleDetails?.contactName || "there";
    const teamMemberName = teamMembers.find(member => member.id === selectedTeamMember)?.name || 'Team';
    
    // Get the shortlist link from the role data
    const role = roles.find(r => r.id === roleData.roleId);
    // Always use a clean shortlist link: https://app.jumpstart-uk.com/shortlists or https://app.jumpstart-uk.com/shortlists?roleId=...
    let shortlistLink = "https://app.jumpstart-uk.com/shortlists";
    if (role && (role["URL ID"] || role["url_id"])) {
      const urlId = role["URL ID"] || role["url_id"];
      shortlistLink = `https://app.jumpstart-uk.com/shortlists?roleId=${urlId}`;
    }

    // Helper function to construct candidate URL with login details
    const getCandidateUrlWithLogin = (candidateUrlId) => {
      // Always return the clean candidate profile URL with no credentials or page params
      return `https://app.jumpstart-uk.com/candidate/${candidateUrlId}`;
    };

    if (selectedCandidateData.length === 0) {
      return `Hey ${contactName}! 👋\n\nNoticed you haven't logged in today, but spotted some candidates I thought you might be interested in for your ${roleTitle} role.\n\nPlease select candidates to include in this template.\n\n${teamMemberName}`;
    }

    let template = `Hey ${contactName}! 👋\n\n`;
    template += `Noticed you haven't logged in today, but spotted some candidates I thought you might be interested in - you can click below to take a look if you're interested?\n\n`;

    selectedCandidateData.forEach((candidate, index) => {
      // Use actual candidate name if available, fallback to extracting from reason, then fallback to generic name
      const actualName = candidate.name || candidate.Name || candidate["Name"] || candidate["Candidate Name"];
      const extractedName = extractNameFromReason(candidate.reason);
      const displayName = actualName || extractedName || `Candidate ${index + 1}`;
      
      // Get candidate profile URL using url_id or fallback to id
      const candidateUrlId = candidate.url_id || candidate.id;
      const candidateProfileUrl = getCandidateUrlWithLogin(candidateUrlId);
      
      console.log(`WhatsApp - Candidate ${displayName} - URL ID:`, candidateUrlId, 'Full candidate:', { id: candidate.id, url_id: candidate.url_id }); // Debug URL ID
      
      template += `• ${displayName} (${candidateProfileUrl})\n`;
    });

    template += `\nCheck them out here: ${shortlistLink}\n\n`;
    template += `Let me know if helpful to chat through!\n\n`;
    template += `${teamMemberName}`;

    return template;
  };

  // Generate email template
  const generateEmailTemplate = (roleData) => {
    const selectedCandidateIds = Object.keys(selectedCandidates[roleData.roleId] || {})
      .filter(id => selectedCandidates[roleData.roleId][id]);
    
    const selectedCandidateData = roleData.candidates.filter(c => 
      selectedCandidateIds.includes(c.id)
    );

    const companyName = roleData.roleDetails?.companyName || "the team";
    const roleTitle = roleData.roleTitle;
    const contactName = roleData.roleDetails?.contactName || "there";
    const teamMemberName = teamMembers.find(member => member.id === selectedTeamMember)?.name || 'Team';
    
    // Get the shortlist link from the role data
    const role = roles.find(r => r.id === roleData.roleId);
    // Always use a clean shortlist link: https://app.jumpstart-uk.com/shortlists or https://app.jumpstart-uk.com/shortlists?roleId=...
    let shortlistLink = "https://app.jumpstart-uk.com/shortlists";
    if (role && (role["URL ID"] || role["url_id"])) {
      const urlId = role["URL ID"] || role["url_id"];
      shortlistLink = `https://app.jumpstart-uk.com/shortlists?roleId=${urlId}`;
    }

    // Helper function to construct candidate URL with login details
    const getCandidateUrlWithLogin = (candidateUrlId) => {
      // Always return the clean candidate profile URL with no credentials or page params
      return `https://app.jumpstart-uk.com/candidate/${candidateUrlId}`;
    };

    if (selectedCandidateData.length === 0) {
      return `Hey ${contactName},\n\nI've created a <a href="${shortlistLink}" target="_blank" style="color: #059669; text-decoration: underline; font-weight: bold;">shortlist of strong candidates</a> that I think would be a great fit for the role.\n\nPlease select candidates to include in this email template.\n\nBest,\n${teamMemberName}`;
    }

    let template = `Hey ${contactName},\n\n`;
    template += `I've created a <a href="${shortlistLink}" target="_blank" style="color: #059669; text-decoration: underline; font-weight: bold;">shortlist of strong candidates</a> that I think would be a great fit for the role.\n\n`;
    template += `<strong>Check out the top ${selectedCandidateData.length} here:</strong>\n\n`;

    template += `<ol style="padding-left: 20px; margin: 0; line-height: 1.4;">`;
    selectedCandidateData.forEach((candidate, index) => {
      // Use actual candidate name if available, fallback to extracting from reason, then fallback to generic name
      const actualName = candidate.name || candidate.Name || candidate["Name"] || candidate["Candidate Name"];
      const extractedName = extractNameFromReason(candidate.reason);
      const displayName = actualName || extractedName || `Candidate ${index + 1}`;
      
      // Get the AI rationale - try multiple possible fields
      const aiReason = candidate.reason || candidate.aiReason || candidate.AIRationale || candidate.aiRationale || 'Strong technical background and relevant experience.';
      
      // Get candidate profile URL using url_id or fallback to id
      const candidateUrlId = candidate.url_id || candidate.id;
      const candidateProfileUrl = getCandidateUrlWithLogin(candidateUrlId);
      
      console.log(`Candidate ${displayName} - AI Reason:`, aiReason); // Debug log
      console.log(`Candidate ${displayName} - URL ID:`, candidateUrlId, 'Full candidate:', { id: candidate.id, url_id: candidate.url_id }); // Debug URL ID
      
      // Create hyperlinked candidate name for email
      const linkedCandidateName = `<a href="${candidateProfileUrl}" target="_blank" style="color: #059669; text-decoration: underline; font-weight: bold;">${displayName}</a>`;
      
      template += `<li style="margin: 0; padding: 0;">${linkedCandidateName}: ${aiReason}</li>`;
    });
    template += `</ol>\n\n`;

    template += `Check out their full profiles along with a few others <a href="${shortlistLink}" target="_blank" style="color: #059669; text-decoration: underline;">here</a>. <strong>Want to talk to any of them?</strong>\n\n`;
    template += `Best,\n${teamMemberName}`;

    return template;
  };

  // Generate templates whenever selections change (auto-sync)
  useEffect(() => {
    if (activeTabRoleId && processedResults[activeTabRoleId]) {
      if (activeTemplate === 'email') {
      const generated = generateEmailTemplate(processedResults[activeTabRoleId]);
      setEditableEmailTemplate(generated);
      } else if (activeTemplate === 'whatsapp') {
        const generated = generateWhatsAppTemplate(processedResults[activeTabRoleId]);
        setEditableEmailTemplate(generated);
    }
    }
  }, [activeTabRoleId, processedResults, selectedCandidates, selectedTeamMember, activeTemplate]);

  // Initialize selected candidates for a role
  const initializeCandidateSelection = (roleId, candidates) => {
    if (!selectedCandidates[roleId]) {
      const initialSelection = {};
      // Select top 5 candidates by default (above 70% match)
      candidates.slice(0, 5).forEach(candidate => {
        if (candidate.score >= 70) {
          initialSelection[candidate.id] = true;
        }
      });
      
      setSelectedCandidates(prev => ({
        ...prev,
        [roleId]: initialSelection
      }));
    }
  };

  // Toggle candidate selection
  const toggleCandidateSelection = (roleId, candidateId) => {
    setSelectedCandidates(prev => ({
      ...prev,
      [roleId]: {
        ...prev[roleId],
        [candidateId]: !prev[roleId]?.[candidateId]
      }
    }));
  };

  // Confirm selection and update Airtable
  const confirmSelection = async (roleId) => {
    if (!processedResults[roleId]) return;

    setIsConfirming(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const roleData = processedResults[roleId];
      const selectedCandidateIds = Object.keys(selectedCandidates[roleId] || {})
        .filter(id => selectedCandidates[roleId][id]);

      if (selectedCandidateIds.length === 0) {
        alert("Please select at least one candidate to confirm");
        return;
      }

      console.log("Confirming selection for role:", roleId);
      console.log("Selected candidate IDs:", selectedCandidateIds);

      // Update the startup's "Recommended Candidates" field
      const response = await axios.post(
        "/api/admin/updateRecommendedCandidates",
        {
          roleId: roleId,
          candidateIds: selectedCandidateIds,
          recommendationData: selectedCandidateIds.reduce((acc, id) => {
            const candidate = roleData.candidates.find(c => c.id === id);
            if (candidate) {
              acc[id] = {
                score: candidate.score,
                reason: candidate.reason
              };
            }
            return acc;
          }, {})
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          }
        }
      );

      console.log("Update response:", response.data);

      // Mark as confirmed
      setProcessedResults(prev => ({
        ...prev,
        [roleId]: {
          ...prev[roleId],
          confirmed: true,
          confirmedAt: new Date().toISOString(),
          selectedCount: selectedCandidateIds.length
        }
      }));

      alert(`Successfully confirmed ${selectedCandidateIds.length} candidates for ${roleData.roleTitle}!`);

    } catch (err) {
      console.error(`Error confirming selection for role ${roleId}:`, err);
      
      // More detailed error handling
      if (err.response) {
        // Server responded with error status
        console.error("Error response:", err.response.data);
        console.error("Error status:", err.response.status);
        setError(`Server error (${err.response.status}): ${err.response.data?.message || err.response.data || "Unknown error"}`);
      } else if (err.request) {
        // Request was made but no response received
        console.error("No response received:", err.request);
        setError("No response from server. Please check your connection and try again.");
      } else {
        // Something else happened
        console.error("Request setup error:", err.message);
        setError(`Error confirming selection: ${err.message}`);
      }
    } finally {
      setIsConfirming(false);
    }
  };

  // Copy email template to clipboard
// Copy template to clipboard
const copyTemplateToClipboard = async () => {
  try {
    if (activeTabRoleId && processedResults[activeTabRoleId]) {
      if (activeTemplate === 'email') {
        // Generate both HTML and plain text versions for email
      const htmlTemplate = generateEmailTemplate(processedResults[activeTabRoleId]);
      const plainTextTemplate = generatePlainTextEmailTemplate(processedResults[activeTabRoleId]);
      
      // Convert the HTML template to proper HTML format for email
      const htmlContent = htmlTemplate.replace(/\n/g, '<br>');
      
      // Use the modern clipboard API to write both HTML and plain text
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([plainTextTemplate], { type: 'text/plain' })
      });
      
      await navigator.clipboard.write([clipboardItem]);
      alert("Email template copied to clipboard (with hyperlinks)!");
      } else {
        // For WhatsApp, copy as plain text with URLs (WhatsApp doesn't support HTML)
        const whatsappTemplate = generateWhatsAppTemplate(processedResults[activeTabRoleId]);
        
        await navigator.clipboard.writeText(whatsappTemplate);
        alert("WhatsApp template copied to clipboard!");
      }
    } else {
      // Fallback for the editable template
      if (activeTemplate === 'email') {
      const htmlContent = editableEmailTemplate.replace(/\n/g, '<br>');
      const plainTextContent = editableEmailTemplate.replace(/<[^>]*>/g, '').replace(/<br>/g, '\n');
      
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([plainTextContent], { type: 'text/plain' })
      });
      
      await navigator.clipboard.write([clipboardItem]);
      alert("Email template copied to clipboard (with hyperlinks)!");
      } else {
        // For WhatsApp fallback, always use plain text (WhatsApp doesn't support HTML)
        const plainTextContent = editableEmailTemplate.replace(/<[^>]*>/g, '').replace(/<br>/g, '\n');
        
        await navigator.clipboard.writeText(plainTextContent);
        alert("WhatsApp template copied to clipboard!");
      }
    }
  } catch (error) {
    console.error('Failed to copy template:', error);
    
    // Fallback to plain text
      await navigator.clipboard.writeText(editableEmailTemplate || emailTemplate);
    alert(`${activeTemplate === 'email' ? 'Email' : 'WhatsApp'} template copied to clipboard (plain text)!`);
  }
};

  // Reset search and selection when clearing
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Fetch roles from Airtable
  useEffect(() => {
    const fetchRoles = async () => {
      setIsLoadingRoles(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("Authentication token not found. Please login again.");
          setIsLoadingRoles(false);
          return;
        }

        // First try the admin endpoint to get all roles
        let response;
        try {
          console.log("Trying admin endpoint to fetch roles from all startups...");
          response = await axios.post("/api/admin/fetchAllRoles", {}, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          console.log("Admin endpoint successful!");
        } catch (adminError) {
          // If admin endpoint fails, fall back to the regular endpoint
          console.error("Admin endpoint failed:", adminError.message);
          console.log("Falling back to regular endpoint to fetch roles just for this startup...");
          
          response = await axios.get("/api/roles/fetchRoles", {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          console.log("Regular endpoint successful");
        }

        // Process the response data
        let allRoles = [];
        if (response.data && response.data.roles && Array.isArray(response.data.roles)) {
          allRoles = response.data.roles;
        } else if (response.data && Array.isArray(response.data)) {
          allRoles = response.data;
        } else {
          throw new Error("Unexpected response format: " + JSON.stringify(response.data));
        }
        
        if (allRoles.length > 0) {
          console.log("Sample role structure:", JSON.stringify(allRoles[0], null, 2));
        }
        
        console.log(`Found ${allRoles.length} total roles`);
        
        // Filter for valid roles (all roles should have an id)
        const validRoles = allRoles.filter(role => role && role.id);
        console.log(`${validRoles.length} roles have valid structure with ID`);
        
        setRoles(validRoles);
      } catch (err) {
        console.error("Error fetching roles:", err);
        setError(`Error fetching roles: ${err.message || "Unknown error"}`);
      } finally {
        setIsLoadingRoles(false);
      }
    };

    const fetchShortlistActivity = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.log("No token available for fetching shortlist activity");
          return;
        }

        console.log("Fetching shortlist activity...");
        const response = await axios.get("/api/candidates/fetchLatestShortlistActivity", {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.data && response.data.activityMap) {
          setShortlistActivity(response.data.activityMap);
          console.log(`Loaded shortlist activity for ${Object.keys(response.data.activityMap).length} roles`);
        }
      } catch (err) {
        console.error("Error fetching shortlist activity:", err);
        // Don't set error state for this as it's not critical
      }
    };

    const fetchRecentRecommendations = async () => {
      setIsLoadingRecentRecommendations(true);
      setRecentRecommendationsError(null);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.log("No token available for fetching recent recommendations");
          return;
        }

        console.log("Fetching recent recommendations...");
        const response = await axios.get("/api/admin/fetchRecentRecommendations", {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.data && response.data.recentRecommendations) {
          setRecentRecommendations(response.data.recentRecommendations);
          console.log(`Loaded ${response.data.recentRecommendations.length} recent recommendations`);
        }
      } catch (err) {
        console.error("Error fetching recent recommendations:", err);
        setRecentRecommendationsError(`Error fetching recent recommendations: ${err.message || "Unknown error"}`);
      } finally {
        setIsLoadingRecentRecommendations(false);
      }
    };

    fetchRoles();
    fetchShortlistActivity();
    fetchRecentRecommendations();
  }, []);

  const handleRoleSelection = (role) => {
    setSelectedRoles(prev => {
      // For this enhanced version, let's allow only 1 role at a time for better focus
      return [role];
    });
  };

  // Utility function to categorize candidates based on score
  const getCandidateCategory = (score) => {
    if (score >= 80) return { category: "Excellent Match", color: "bg-green-100 border-green-500 text-green-800" };
    if (score >= 60) return { category: "Good Match", color: "bg-blue-100 border-blue-500 text-blue-800" };
    return { category: "Match", color: "bg-gray-100 border-gray-500 text-gray-800" };
  };

  const startRecommendationProcess = async () => {
    if (selectedRoles.length === 0) {
      alert("Please select a role");
      return;
    }

    // NOTE: This function is now deprecated in favor of the tag review flow
    // New flow: fetchRecommendationCriteria -> TagReviewModal -> startRecommendationProcessWithAnalysis
    console.warn("startRecommendationProcess called - this should use the tag review flow instead");

    setIsProcessing(true);
    setActiveRecommendations({});
    setProcessedResults({});
    setEmailTemplate('');
    
    // Process the selected role
    const recommendations = {};

    try {
      const role = selectedRoles[0]; // Only one role in enhanced version
        const roleTitle = role["Role Title"] || "Unknown Role";
        
        recommendations[role.id] = { 
          status: 'processing',
          phase: 1,
          progress: 0,
          roleTitle: roleTitle, 
          error: null,
          data: null
        };
        setActiveRecommendations({...recommendations});
        
        await processRoleRecommendations(role.id, recommendations);
    } catch (err) {
      console.error("Error in recommendation process:", err);
      setError(`Error in recommendation process: ${err.message || "Unknown error"}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const processRoleRecommendations = async (roleId, recommendations) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      // Find the role by ID to get its title
      const role = roles.find(r => r.id === roleId);
      const roleTitle = role ? (role["Role Title"] || "Unknown Role") : "Unknown Role";

      // Update recommendations with role title
      recommendations[roleId] = {
        ...recommendations[roleId],
        status: 'processing',
        phase: 1,
        progress: 10,
        roleTitle: roleTitle
      };
      setActiveRecommendations({...recommendations});

      // Use the same API endpoint and parameters as in browse-test.js
      const phase1Response = await axios.post(
        "/api/candidates/getRecommendations",
        { roleId, phase: 1 },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 600000 // 10 minutes timeout for long-running processes
        }
      );

      // Update progress
      recommendations[roleId].progress = 50;
      setActiveRecommendations({...recommendations});

      if (!phase1Response.data || !phase1Response.data.topCandidatesByEmbedding) {
        throw new Error("No candidates found in phase 1");
      }

      // Phase 2 - GPT analysis
      recommendations[roleId] = {
        ...recommendations[roleId],
        phase: 2,
        progress: 60,
      };
      setActiveRecommendations({...recommendations});

      const phase2Response = await axios.post(
        "/api/candidates/getRecommendations",
        { 
          roleId, 
          phase: 2,
          candidateEmbeddings: phase1Response.data.topCandidatesByEmbedding
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 600000
        }
      );

      if (!phase2Response.data || !phase2Response.data.recommendedCandidates) {
        throw new Error("No candidates found in phase 2");
      }

      // Create recommendation map for all candidates (up to 30)
      const recommendationMap = {};
      if (phase2Response.data.allRankedCandidates && phase2Response.data.allRankedCandidates.length > 0) {
        phase2Response.data.allRankedCandidates.forEach(candidate => {
          if (candidate && candidate.id) {
            recommendationMap[candidate.id] = {
              score: candidate.score,
              reason: candidate.reason,
              isTopGPTCandidate: phase2Response.data.recommendedCandidates.some(rc => rc.id === candidate.id)
            };
          }
        });
      }

      // Store results for display - include all candidates up to 30
      const processedResult = {
        roleId,
        roleTitle: recommendations[roleId].roleTitle,
        candidates: phase2Response.data.allRankedCandidates || [],
        topCandidates: phase2Response.data.recommendedCandidates || [],
        roleDetails: phase2Response.data.roleDetails,
        phase1Data: phase1Response.data,
        phase2Data: phase2Response.data,
        recommendationMap,
        saved: false
      };

      setProcessedResults(prev => ({
        ...prev,
        [roleId]: processedResult
      }));

      // Initialize candidate selection
      initializeCandidateSelection(roleId, processedResult.candidates);

      recommendations[roleId] = {
        ...recommendations[roleId],
        status: 'completed',
        progress: 100,
        data: processedResult
      };
      setActiveRecommendations({...recommendations});

      // Set as active tab
        setActiveTabRoleId(roleId);

    } catch (err) {
      console.error(`Error processing role ${roleId}:`, err);
      recommendations[roleId] = {
        ...recommendations[roleId],
        status: 'error',
        error: err.message || "Unknown error",
        progress: 0
      };
      setActiveRecommendations({...recommendations});
    }
  };

  // Process role recommendations with JD analysis
  const processRoleRecommendationsWithAnalysis = async (roleId, recommendations, jdAnalysis, gender, selectedStream, requireNoVisa = false) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      // Find the role by ID to get its title
      const role = roles.find(r => r.id === roleId);
      const roleTitle = role ? (role["Role Title"] || "Unknown Role") : "Unknown Role";

      // Update recommendations with role title
      recommendations[roleId] = {
        ...recommendations[roleId],
        status: 'processing',
        phase: 1,
        progress: 10,
        roleTitle: roleTitle
      };
      setActiveRecommendations({...recommendations});

      // Use the same API endpoint and parameters as in browse-test.js, but include JD analysis
      const phase1Response = await axios.post(
        "/api/candidates/getRecommendations",
        { 
          roleId, 
          phase: 1,
          customJDAnalysis: jdAnalysis, // Fixed: API expects 'customJDAnalysis', not 'jdAnalysis'
          gender: gender, // Add gender parameter
          customStream: selectedStream, // Add custom stream parameter for roles without a stream
          requireNoVisa: requireNoVisa // Add visa filter parameter
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 600000 // 10 minutes timeout for long-running processes
        }
      );

      // Update progress
      recommendations[roleId].progress = 50;
      setActiveRecommendations({...recommendations});

      if (!phase1Response.data || !phase1Response.data.topCandidatesByEmbedding) {
        throw new Error("No candidates found in phase 1");
      }

      // Phase 2 - GPT analysis
      recommendations[roleId] = {
        ...recommendations[roleId],
        phase: 2,
        progress: 60,
      };
      setActiveRecommendations({...recommendations});

      const phase2Response = await axios.post(
        "/api/candidates/getRecommendations",
        { 
          roleId, 
          phase: 2,
          candidateEmbeddings: phase1Response.data.topCandidatesByEmbedding,
          customJDAnalysis: jdAnalysis, // Fixed: API expects 'customJDAnalysis', not 'jdAnalysis'
          gender: gender, // Add gender parameter
          customStream: selectedStream, // Add custom stream parameter for roles without a stream
          requireNoVisa: requireNoVisa // Add visa filter parameter
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 600000
        }
      );

      if (!phase2Response.data || !phase2Response.data.recommendedCandidates) {
        throw new Error("No candidates found in phase 2");
      }

      // Create recommendation map for all candidates (up to 30)
      const recommendationMap = {};
      if (phase2Response.data.allRankedCandidates && phase2Response.data.allRankedCandidates.length > 0) {
        phase2Response.data.allRankedCandidates.forEach(candidate => {
          if (candidate && candidate.id) {
            recommendationMap[candidate.id] = {
              score: candidate.score,
              reason: candidate.reason,
              isTopGPTCandidate: phase2Response.data.recommendedCandidates.some(rc => rc.id === candidate.id)
            };
          }
        });
      }

      // Store results for display - include all candidates up to 30
      const processedResult = {
        roleId,
        roleTitle: recommendations[roleId].roleTitle,
        candidates: phase2Response.data.allRankedCandidates || [],
        topCandidates: phase2Response.data.recommendedCandidates || [],
        roleDetails: phase2Response.data.roleDetails,
        phase1Data: phase1Response.data,
        phase2Data: phase2Response.data,
        recommendationMap,
        jdAnalysis: jdAnalysis, // Store the JD analysis used
        saved: false
      };

      setProcessedResults(prev => ({
        ...prev,
        [roleId]: processedResult
      }));

      // Initialize candidate selection
      initializeCandidateSelection(roleId, processedResult.candidates);

      recommendations[roleId] = {
        ...recommendations[roleId],
        status: 'completed',
        progress: 100,
        data: processedResult
      };
      setActiveRecommendations({...recommendations});

      // Set as active tab
        setActiveTabRoleId(roleId);

    } catch (err) {
      console.error(`Error processing role ${roleId}:`, err);
      recommendations[roleId] = {
        ...recommendations[roleId],
        status: 'error',
        error: err.message || "Unknown error",
        progress: 0
      };
      setActiveRecommendations({...recommendations});
    }
  };

  const saveRecommendationsToAirtable = async (roleId) => {
    if (!processedResults[roleId]) {
      setError(`No processed results found for role ${roleId}`);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const roleData = processedResults[roleId];
      
      // Get the candidate IDs from the recommendation map (all candidates, not just top ones)
      const candidateIds = Object.keys(roleData.recommendationMap);
      
      // Save to Airtable using the existing endpoint
      const response = await axios.post(
        "/api/candidates/saveRecommendationsToNewTables",
        {
          roleId,
          candidates: candidateIds,
          recommendationData: roleData.recommendationMap,
          jdAnalysis: roleData.roleDetails?.jdAnalysis || null
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          }
        }
      );

      // Update the results to mark as saved
      setProcessedResults(prev => ({
        ...prev,
        [roleId]: {
          ...prev[roleId],
          saved: true,
          saveResponse: response.data
        }
      }));

      alert(`Recommendations for ${roleData.roleTitle} saved successfully!`);

    } catch (err) {
      console.error(`Error saving recommendations for role ${roleId}:`, err);
      setError(`Error saving recommendations: ${err.message || "Unknown error"}`);
    }
  };

  const deleteRecommendation = (roleId) => {
    // Just remove from UI, don't delete from database
    setProcessedResults(prev => {
      const updated = {...prev};
      delete updated[roleId];
      return updated;
    });

    // Clear selections for this role
    setSelectedCandidates(prev => {
      const updated = {...prev};
      delete updated[roleId];
      return updated;
    });

    // Clear email template if it was for this role
    setEmailTemplate('');

    // Update active tab if needed
    if (activeTabRoleId === roleId) {
      const remainingRoleIds = Object.keys(processedResults).filter(id => id !== roleId);
      setActiveTabRoleId(remainingRoleIds.length > 0 ? remainingRoleIds[0] : null);
    }
  };

  // Component to display loading progress
  const ProgressBar = ({ progress }) => (
    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
      <div 
        className="bg-green-600 h-2.5 rounded-full" 
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );

  // Filter options for the popup
  const filterOptions = [
    { id: 'live', label: 'Live', description: 'Currently active roles' },
    { id: 'live_last_week', label: 'Live last week', description: 'Recently active roles' },
    { id: 'preview', label: 'Preview (ignore mo testing)', description: 'Preview status roles' },
    { id: 'in_process', label: 'In-Process', description: 'Roles currently in process' },
    { id: 'live_low_apps', label: 'Live (Under 10 Apps)', description: 'Active roles with few applications' },
    { id: 'live_last_week_low_apps', label: 'Live last week (Under 10 Apps)', description: 'Recent roles with few applications' },
    { id: 'owner_martha', label: 'Martha\'s Roles', description: 'Roles assigned to Martha' },
    { id: 'owner_darcy', label: 'Darcy\'s Roles', description: 'Roles assigned to Darcy' },
    { id: 'owner_matthew', label: 'Matthew\'s Roles', description: 'Roles assigned to Matthew' },
    { id: 'owner_bethan', label: 'Bethan\'s Roles', description: 'Roles assigned to Bethan' },
    { id: 'owner_rachel', label: 'Rachel\'s Roles', description: 'Roles assigned to Rachel' },
    { id: 'owner_jared', label: 'Jared\'s Roles', description: 'Roles assigned to Jared' }
  ];

  // Toggle filter selection
  const toggleFilter = (filterId) => {
    if (filterId.startsWith('owner_')) {
      // For owner filters, use single selection (radio behavior)
      setSelectedFilters(prev => {
        const nonOwnerFilters = prev.filter(id => !id.startsWith('owner_'));
        if (prev.includes(filterId)) {
          // If already selected, deselect it
          return nonOwnerFilters;
        } else {
          // Select this owner and keep non-owner filters
          return [...nonOwnerFilters, filterId];
        }
      });
    } else {
      // For non-owner filters, use multi-selection (checkbox behavior)
      setSelectedFilters(prev => 
        prev.includes(filterId) 
          ? prev.filter(id => id !== filterId)
          : [...prev, filterId]
      );
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedFilters([]);
  };

  // Apply filters and close popup
  const applyFilters = () => {
    setShowFiltersPopup(false);
  };

  // Fetch recommendation criteria (JD analysis) for a role
  const fetchRecommendationCriteria = async (role) => {
    setIsLoadingJDAnalysis(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      console.log(`Fetching JD analysis for role: ${role.id}`);
      
      const response = await axios.post(
        "/api/candidates/analyzeJobDescription",
        { roleId: role.id, useAccountManagerComment: true },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 60000 // 1 minute timeout for JD analysis
        }
      );

      console.log("JD Analysis response:", response.data);

      if (response.data.success && response.data.jdAnalysis) {
        setCurrentJDAnalysis(response.data.jdAnalysis);
        setSelectedRoleForReview(role);
        setShowTagReviewModal(true);
      } else {
        throw new Error("No JD analysis returned from server");
      }
    } catch (err) {
      console.error("Error fetching recommendation criteria:", err);
      setError(`Error analyzing job description: ${err.message || "Unknown error"}`);
      
      // Fallback: show modal with empty analysis
      setCurrentJDAnalysis({
        must_have: "",
        nice_to_have: "",
        experience: "",
        industryFocus: ""
      });
      setSelectedRoleForReview(role);
      setShowTagReviewModal(true);
    } finally {
      setIsLoadingJDAnalysis(false);
    }
  };

  // Handle proceeding from tag review modal
  const handleTagReviewProceed = async (updatedJDAnalysis, isSingleSentenceSearch, gender, selectedStream, requireNoVisa = false) => {
    setShowTagReviewModal(false);
    
    if (!selectedRoleForReview) {
      setError("No role selected for recommendations");
      return;
    }

    // Store the updated JD analysis for use in recommendations
    console.log("Proceeding with updated JD analysis:", updatedJDAnalysis);
    console.log("Selected stream:", selectedStream);
    console.log("Require no visa:", requireNoVisa);
    
    // Set the selected role and start recommendations
    setSelectedRoles([selectedRoleForReview]);
    await startRecommendationProcessWithAnalysis(selectedRoleForReview, updatedJDAnalysis, gender, selectedStream, requireNoVisa);
  };

  // Close tag review modal
  const handleTagReviewClose = () => {
    setShowTagReviewModal(false);
    setCurrentJDAnalysis(null);
    setSelectedRoleForReview(null);
  };
  
  // Handle edit and re-run functionality
  const handleEditAndRerun = () => {
    if (currentSearchCriteria.role && currentSearchCriteria.jdAnalysis) {
      setSelectedRoleForReview(currentSearchCriteria.role);
      setCurrentJDAnalysis(currentSearchCriteria.jdAnalysis);
      setShowTagReviewModal(true);
    }
  };

  // Save selected candidates to role's shortlist with rationales
  const saveToShortlist = async (roleId) => {
    if (!processedResults[roleId]) return;

    setIsConfirming(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const roleData = processedResults[roleId];
      const selectedCandidateIds = Object.keys(selectedCandidates[roleId] || {})
        .filter(id => selectedCandidates[roleId][id]);

      if (selectedCandidateIds.length === 0) {
        alert("Please select at least one candidate to add to shortlist");
        return;
      }

      console.log("Saving to shortlist with rationales:", { roleId, selectedCandidateIds });

      // Prepare candidates with rationales for the new API
      const candidatesWithRationales = selectedCandidateIds.map(candidateId => {
        const candidate = roleData.candidates.find(c => c.id === candidateId);
        return {
          candidateId,
          rationale: candidate?.reason || "AI recommended candidate",
          score: candidate?.score || 70,
          sourceRecommendationId: null // Could link to original recommendation if needed
        };
      });

      // Get team member name for tracking
      const teamMemberName = teamMembers.find(member => member.id === selectedTeamMember)?.name || 'Team';

      // Use the new API endpoint that saves both shortlist and rationales
      const response = await axios.post(
        "/api/candidates/saveShortlistWithRationales",
        {
          roleId,
          candidatesWithRationales,
          addedBy: teamMemberName
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          }
        }
      );

      console.log("API response:", response.data);

      // Update local state to reflect the changes
      setProcessedResults(prev => ({
        ...prev,
        [roleId]: {
          ...prev[roleId],
          shortlisted: true,
          shortlistedAt: new Date().toISOString(),
          shortlistedCount: selectedCandidateIds.length,
          shortlistDetailsCreated: response.data.shortlistDetailsCreated || false
        }
      }));

      // Update the roles array to reflect the new shortlisted candidates
      const role = roles.find(r => r.id === roleId);
      if (role) {
        const currentShortlistedCandidates = role["Shortlisted Candidates"] || [];
        const updatedShortlistedCandidates = [
          ...new Set([...currentShortlistedCandidates, ...selectedCandidateIds])
        ];
        
        setRoles(prevRoles => {
          return prevRoles.map(r => 
            r.id === roleId 
              ? { ...r, "Shortlisted Candidates": updatedShortlistedCandidates } 
              : r
          );
        });
      }

      // Show appropriate success message based on what succeeded
      const { roleUpdateSuccess, shortlistDetailsCreated, details } = response.data;
      
      if (roleUpdateSuccess && shortlistDetailsCreated) {
        alert(`✅ Success! Added ${selectedCandidateIds.length} candidates to the shortlist for ${roleData.roleTitle} with their AI rationales!`);
      } else if (shortlistDetailsCreated && !roleUpdateSuccess) {
        alert(`✅ AI rationales saved successfully for ${selectedCandidateIds.length} candidates!\n\n⚠️ Note: Could not update the main shortlist due to permissions on the ALL ROLES table. The rationales are saved and will be available when viewing the shortlist.`);
      } else if (roleUpdateSuccess && !shortlistDetailsCreated) {
        alert(`✅ Added ${selectedCandidateIds.length} candidates to the shortlist for ${roleData.roleTitle}!\n\n⚠️ Note: AI rationales could not be saved. Check ShortlistDetails table permissions.`);
      } else {
        alert(`⚠️ Partial success: Attempted to save ${selectedCandidateIds.length} candidates.\n\nIssues encountered:\n- ${details?.roleUpdate}\n- ${details?.rationales}\n\nPlease check Airtable permissions.`);
      }

      // Refresh shortlist activity to show the latest data
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const activityResponse = await axios.get("/api/candidates/fetchLatestShortlistActivity", {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (activityResponse.data && activityResponse.data.activityMap) {
            setShortlistActivity(activityResponse.data.activityMap);
          }
        }
      } catch (activityErr) {
        console.error("Error refreshing shortlist activity:", activityErr);
        // Don't throw error - this is not critical
      }

    } catch (err) {
      console.error(`Error saving candidates to shortlist for role ${roleId}:`, err);
      
      if (err.response) {
        console.error("Error response:", err.response.data);
        console.error("Error status:", err.response.status);
        setError(`Server error (${err.response.status}): ${err.response.data?.message || err.response.data || "Unknown error"}`);
      } else if (err.request) {
        console.error("No response received:", err.request);
        setError("No response from server. Please check your connection and try again.");
      } else {
        console.error("Request setup error:", err.message);
        setError(`Error saving to shortlist: ${err.message}`);
      }
    } finally {
      setIsConfirming(false);
    }
  };

  // Start recommendation process with JD analysis from tag review
  const startRecommendationProcessWithAnalysis = async (role, jdAnalysis, gender, selectedStream, requireNoVisa = false) => {
    setIsProcessing(true);
    setActiveRecommendations({});
    setProcessedResults({});
    setEmailTemplate('');
    
    // Store current search criteria for edit functionality
    setCurrentSearchCriteria({
      jdAnalysis,
      gender,
      selectedStream,
      requireNoVisa,
      role
    });
    
    // Process the selected role with the updated JD analysis
    const recommendations = {};

    try {
      const roleTitle = role["Role Title"] || "Unknown Role";
        
      recommendations[role.id] = { 
        status: 'processing',
        phase: 1,
        progress: 0,
        roleTitle: roleTitle, 
        error: null,
        data: null,
        jdAnalysis: jdAnalysis // Store the JD analysis for use in recommendations
      };
      setActiveRecommendations({...recommendations});
      
      await processRoleRecommendationsWithAnalysis(role.id, recommendations, jdAnalysis, gender, selectedStream, requireNoVisa);
    } catch (err) {
      console.error("Error in recommendation process:", err);
      setError(`Error in recommendation process: ${err.message || "Unknown error"}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Load saved recommendations and display them in the same interface
  const loadSavedRecommendations = async (roleId, roleTitle, companyName, recId, recCreatedAt) => {
    setIsLoadingSavedRecommendations(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      console.log(`Loading saved recommendations for role ${roleId}`);
      
      // Clear previous results
      setProcessedResults({});
      setSelectedCandidates({});
      setEditableEmailTemplate('');
      setActiveTabRoleId(null);

      // Try to fetch recommendations for this specific recommendation run (recId)
      const urlWithRec = `/api/candidates/fetchRecommendations?roleId=${roleId}${recId ? `&recId=${encodeURIComponent(recId)}` : ''}${recCreatedAt ? `&createdAt=${encodeURIComponent(recCreatedAt)}` : ''}`;
      let recommendationsResponse;
      try {
        recommendationsResponse = await axios.get(urlWithRec, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        // If this specific run has no details (HTTP 404), gracefully fallback to role-wide recommendations
        if (err?.response?.status === 404) {
          console.warn(`No details linked to recId. Falling back to role-only recommendations for role ${roleId}`);
          const urlRoleOnly = `/api/candidates/fetchRecommendations?roleId=${roleId}`;
          recommendationsResponse = await axios.get(urlRoleOnly, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } else {
          throw err;
        }
      }

      const { candidateIds, recommendationMap } = recommendationsResponse.data;
      console.log(`Retrieved ${candidateIds?.length || 0} candidate IDs and ${recommendationMap?.length || 0} recommendation mappings`);

      if (!candidateIds || candidateIds.length === 0) {
        throw new Error("No saved recommendations found for this role");
      }

      // Fetch the actual candidate records using POST to avoid URL length limits
      const candidatesResponse = await axios.post(
        `/api/candidates/fetchCandidates`,
        {
          ids: candidateIds
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!candidatesResponse.data || !candidatesResponse.data.candidates) {
        throw new Error("Failed to fetch candidate details");
      }

      // Transform and enrich the candidates with recommendation data
      const fetchedCandidates = candidatesResponse.data.candidates;
      console.log(`Processing ${fetchedCandidates.length} candidates`);

      // Create recommendation map
      const recMap = {};
      recommendationMap.forEach(rec => {
        recMap[rec.candidateId] = {
          score: rec.score || 80,
          reason: rec.reason || "AI recommended candidate",
          isTopGPTCandidate: true,
        };
      });

      // Helper functions (same as browse-test.js)
      const transformRoles = (roles) => {
        const roleSet = new Set(roles);
        if (roleSet.size === 1) {
          if (roleSet.has("Full Stack Developer")) return ["Full Stack"];
          if (roleSet.has("Front End Developer")) return ["Front End"];
          if (roleSet.has("Back End Developer")) return ["Back End"];
        }
        if (roleSet.has("Front End Developer") && roleSet.has("Back End Developer")) {
          return ["Full Stack"];
        }
        if (roleSet.has("Full Stack Developer")) {
          return ["Full Stack"];
        }
        return roles;
      };

      const parseSalaryRange = (salary) => {
        const salaryRange = salary.match(/£(\d+)k?-?£?(\d+)?k?/i);
        if (salaryRange) {
          const minSalary = Number.parseInt(salaryRange[1]) * 1000;
          const maxSalary = salaryRange[2]
            ? Number.parseInt(salaryRange[2]) * 1000
            : minSalary;
          return { minSalary, maxSalary };
        }
        return { minSalary: null, maxSalary: null };
      };

      const formatSalaryRange = (minimum, ideal) => {
        if (!minimum && !ideal) return null;
        
        // Helper to clean and parse salary values
        const cleanSalary = (val) => {
          if (!val) return null;
          // Remove any non-numeric characters and convert to number
          const num = val.toString().replace(/[^0-9.]/g, '');
          return num ? parseInt(num, 10) : null;
        };
        
        const min = cleanSalary(minimum);
        const max = cleanSalary(ideal);
        
        if (min && max) {
          if (min === max) {
            return `£${min}k`;
          } else {
            return `£${min}k - £${max}k`;
          }
        } else if (min) {
          return `£${min}k+`;
        } else if (max) {
          return `Up to £${max}k`;
        }
        
        return null;
      };

      // Full candidate transformation (same as browse-test.js)
      const transformCandidateFull = (candidate) => {
        if (!candidate || !candidate.fields) {
          return {
            id: candidate?.id || "unknown-id",
            name: candidate?.name || "No data",
            stream: candidate?.stream || "",
          };
        }

        const fields = candidate.fields || {};

        // Safely handle SWE roles
        let sweRoles = [];
        try {
          const rawSweRoles = fields["SWE relevant roles"] || "";
          if (rawSweRoles) {
            sweRoles = Array.isArray(rawSweRoles)
              ? rawSweRoles[0].split(",").map(role => role.trim())
              : typeof rawSweRoles === 'string'
                ? rawSweRoles.split(",").map(role => role.trim())
                : [];
            sweRoles = transformRoles(sweRoles);
          }
        } catch (error) {
          console.error("Error processing SWE roles:", error);
        }

        // Safely extract stream
        let stream = "";
        try {
          if (fields["Stream (from CRM)"]) {
            stream = Array.isArray(fields["Stream (from CRM)"])
              ? fields["Stream (from CRM)"][0]
              : fields["Stream (from CRM)"];
          }
        } catch (error) {
          console.error("Error processing stream:", error);
        }

        // Safely determine salary
        let salary = "";
        try {
          if (stream === "Generalist" || stream === "Marketing") {
            salary = fields["Salary - cleaned"] || "";
          } else if (stream === "Sales") {
            salary = fields["Base range + commission"] || "";
          } else if (stream === "Software Engineering") {
            salary = fields["SWE Salary - cleaned"] || "";
          }
        } catch (error) {
          console.error("Error determining salary:", error);
        }

        const { minSalary, maxSalary } = parseSalaryRange(salary);

        // Get image URL safely
        const imageUrl = fields.Photo && fields.Photo[0] ? fields.Photo[0].url : null;

        return {
          id: candidate.id || "",
          url_id: fields["ID"] || "",
          minimum_salary: fields["Minimum salary"] || "",
          ideal_salary: fields["Ideal salary"] || "",
          salary_minimum: fields["Minimum salary"] || "",
          salary_ideal: fields["Ideal salary"] || "",
          expectedSalaryRange: formatSalaryRange(fields["Minimum salary"], fields["Ideal salary"]),

          createdTime: candidate.createdTime || "",
          years_of_exp: fields["Years of exp (or commercial exp)"] || 0,
          cv: fields["CV RAW"] || "",
          video: fields["Video intro (new)"] || "",
          linkedin: fields["LinkedIn RAW"] || "",
          github: fields["Github RAW"] || "",
          
          name: fields["Name"] || "",
          lastName: fields["Surname"] || "",
          email: fields["Email"] || "",
          
          undergrad_degree_type: fields["Undergrad degree type"] || "",
          undergrad_degree_subject: fields["Undergrad subject"] || "",
          undergrad_university: fields["Undergrad university"] || "",
          undergrad_graduation_year: fields["Undergrad graduation year"] || "",
          undergrad_result: fields["Undergrad result"] || "",
          postgrad_degree_type: fields["Postgrad degree type"] || "",
          postgrad_degree_subject: fields["Postgrad subject"] || "",
          postgrad_university: fields["Postgrad university"] || "",
          postgrad_graduation_year: fields["Postgrad graduation year"] || "",
          postgrad_result: fields["Postgrad result"] || "",
          
          roles: fields["Generalist roles suitable for copy"] || "",
          industry_exp: fields["Industry experience new"] || [],
          stream: stream,
          search_fields: fields["Search Aggregate Fields"] || "",

          // Additional fields used by the card
          description: fields["Description"] || fields["Top achievement"] || "",
          most_impressive_swe: fields["Most impressive swe"] || "",
          right_to_work: fields["RTW doc"] || "",
          recentTarget: fields["Recent target achieved"] || "",
          mostRecentCohort: fields["Most recent cohort"] || "",
          
          working_style: fields["Preferred working style copy"] || "",
          languages: fields["Languages copy"] || "",
          time_coding: fields["Time spent coding"] || "",
          coding_languages: fields["Coding languages new"] || "",
          
          // Add Jumpstart view (Sales) field
          "Jumpstart view (Sales)": fields["Jumpstart view (Sales)"] || "",
          
          work_experience_1_role: fields["(1) Work Experience Role"] || "",
          work_experience_1_company: fields["(1) Work Experience Company"] || "",
          work_experience_1_description: fields["(1) Work Experience Description"] || "",
          work_experience_1_duration: {
            years: fields["(1) Time spent in work experience YRS"] || 0,
            months: fields["(1) Time spent in work experience MOS"] || 0,
          },
          work_experience_2_role: fields["(2) Work Experience Role"] || "",
          work_experience_2_company: fields["(2) Work Experience Company"] || "",
          work_experience_2_description: fields["(2) Work Experience Description"] || "",
          work_experience_2_duration: {
            years: fields["(2) Time spent in work experience YRS"] || 0,
            months: fields["(2) Time spent in work experience MOS"] || 0,
          },

          swe_roles: sweRoles,
          sales: fields["Proven Sales Experience / High Potential for Sales"] || "",
          exp_swe: fields["Level of exp SWE"] || "",
          
          // Important: match the exact property names the card expects
          portfolio_swe: fields["SWE Portfolio"] || "",
          marketing_portfolio: fields["Marketing portfolio RAW"] || "",
          
          image: imageUrl,
        };
      };

      // Transform candidates using the comprehensive transformation
      const transformedCandidates = fetchedCandidates.map(candidate => {
        try {
          const transformedCandidate = transformCandidateFull(candidate);
          
          // Add recommendation data
          const rec = recMap[candidate.id] || {};
          return {
            ...transformedCandidate,
            score: rec.score || 0,
            reason: rec.reason || "",
            recommendationScore: rec.score || 0,
            recommendationReason: rec.reason || "",
            isFiltered: false,
          };
        } catch (error) {
          console.error(`Error transforming candidate ${candidate.id}:`, error);
          // Return a minimal valid object with the ID preserved
          return {
            id: candidate.id,
            name: candidate.fields?.Name || "Error transforming candidate",
            stream: candidate.fields?.["Stream (from CRM)"] || "",
            score: recMap[candidate.id]?.score || 0,
            reason: recMap[candidate.id]?.reason || "",
            recommendationScore: recMap[candidate.id]?.score || 0,
            recommendationReason: recMap[candidate.id]?.reason || "",
            isFiltered: false,
          };
        }
      });

      // Sort by recommendation score (highest first)
      const sortedCandidates = transformedCandidates.sort((a, b) => {
        const scoreA = a.recommendationScore || 0;
        const scoreB = b.recommendationScore || 0;
        return scoreB - scoreA;
      });

      // Create processed result in the same format as new recommendations
      const processedResult = {
        roleId,
        roleTitle: roleTitle || "Unknown Role",
        candidates: sortedCandidates,
        topCandidates: sortedCandidates.filter(c => c.recommendationScore >= 70), // Consider 70+ as top candidates
        roleDetails: {
          companyName: companyName || "Unknown Company",
          contactName: "Team"
        },
        recommendationMap: recMap,
        saved: true, // Mark as already saved
        isLoadedFromSaved: true // Flag to indicate this is loaded from saved recommendations
      };

      // Set the processed results
      setProcessedResults({
        [roleId]: processedResult
      });

      // Initialize candidate selection for top candidates
      initializeCandidateSelection(roleId, sortedCandidates);

      // Set as active tab
      setActiveTabRoleId(roleId);

      console.log(`Successfully loaded ${sortedCandidates.length} saved recommendations for ${roleTitle}`);

    } catch (err) {
      console.error("Error loading saved recommendations:", err);
      setError(`Error loading saved recommendations: ${err.message || "Unknown error"}`);
    } finally {
      setIsLoadingSavedRecommendations(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Clean Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Role Recommendations</h1>
              <p className="text-gray-600 mt-2">Generate AI recommendations for startup roles</p>
            </div>
          </div>
        </div>
      </div>
      
      {error && (
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
          <strong>Error:</strong> {error}
              </div>
          <button 
                className="text-red-700 hover:text-red-900" 
            onClick={() => setError(null)}
          >
                ×
          </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Role Selection View */}
        {Object.keys(processedResults).length === 0 && !isProcessing && (
          <div className="space-y-8">
            {/* Team Member Selector */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Which team member are you?</h3>
              <div className="flex space-x-3">
                {teamMembers.map(member => (
                  <button
                    key={member.id}
                    onClick={() => setSelectedTeamMember(member.id)}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                      selectedTeamMember === member.id
                        ? 'bg-green-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {member.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Recommendations Section */}
            <div className="bg-white rounded-xl border border-gray-100">
              <div className="flex items-center justify-between p-6">
                <div>
                  <button
                    className="text-left"
                    onClick={() => setIsRecentCollapsed(prev => !prev)}
                    aria-expanded={!isRecentCollapsed}
                    aria-controls="recent-recs-panel"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">Recent recommendations</h3>
                    <p className="text-sm text-gray-500">You can view recommendations that have been automated here. Roles with AI suggestions in the past week.</p>
                  </button>
                </div>
                <button
                  onClick={() => {
                    setIsLoadingRecentRecommendations(true);
                    setRecentRecommendationsError(null);
                    const fetchRecentRecommendations = async () => {
                      try {
                        const token = localStorage.getItem("token");
                        if (!token) return;
                        const response = await axios.get("/api/admin/fetchRecentRecommendations", {
                          headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                          },
                        });
                        if (response.data && response.data.recentRecommendations) {
                          setRecentRecommendations(response.data.recentRecommendations);
                        }
                      } catch (err) {
                        setRecentRecommendationsError(`Error refreshing: ${err.message || "Unknown error"}`);
                      } finally {
                        setIsLoadingRecentRecommendations(false);
                      }
                    };
                    fetchRecentRecommendations();
                  }}
                  className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium border transition-colors ${
                    isLoadingRecentRecommendations
                      ? 'border-gray-200 text-gray-400 bg-white'
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                  disabled={isLoadingRecentRecommendations}
                  aria-busy={isLoadingRecentRecommendations}
                >
                  <span>{isLoadingRecentRecommendations ? 'Refreshing…' : 'Refresh'}</span>
                </button>
              </div>

              {recentRecommendationsError && (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {recentRecommendationsError}
                </div>
              )}

              {!isRecentCollapsed && (isLoadingRecentRecommendations ? (
                <div className="space-y-3">
                  {[0,1,2].map((i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border border-gray-100 p-4 animate-pulse">
                      <div className="flex-1">
                        <div className="h-4 w-56 bg-gray-100 rounded" />
                        <div className="mt-3 flex items-center gap-3 text-sm">
                          <div className="h-3 w-24 bg-gray-100 rounded" />
                          <div className="h-3 w-20 bg-gray-100 rounded" />
                        </div>
                      </div>
                      <div className="h-8 w-36 bg-gray-100 rounded" />
                    </div>
                  ))}
                </div>
              ) : recentRecommendations.length > 0 ? (
                <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                  {recentRecommendations.map((rec) => (
                    <div
                      key={rec.recId}
                      className="group flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-3">
                          <h4 className="truncate font-medium text-gray-900">{rec.roleTitle}</h4>
                          <span className="shrink-0 text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 ring-1 ring-inset ring-green-200">
                            {rec.candidateCount} candidate{rec.candidateCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                          <span className="truncate">{rec.companyName}</span>
                          <span className="text-gray-300">•</span>
                          <span>
                            {(() => {
                              const date = new Date(rec.createdAt);
                              const now = new Date();
                              const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
                              if (diffInHours < 1) return 'Less than an hour ago';
                              if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
                              const diffInDays = Math.floor(diffInHours / 24);
                              if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
                              return date.toLocaleDateString('en-GB');
                            })()}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          loadSavedRecommendations(rec.roleId, rec.roleTitle, rec.companyName, rec.recId, rec.createdAt);
                        }}
                        className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-green-700 hover:text-green-800 ring-1 ring-inset ring-green-200 hover:ring-green-300 bg-green-50/50 hover:bg-green-50 transition-colors"
                        disabled={isLoadingSavedRecommendations}
                      >
                        <span>{isLoadingSavedRecommendations ? 'Loading…' : 'View recommendations'}</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <h4 className="text-base font-medium text-gray-900">No recent recommendations</h4>
                  <p className="mt-1 text-sm text-gray-500">Nothing in the last 7 days.</p>
                </div>
              ))}
            </div>

            {/* Search and Filters */}
            <div className="space-y-6">
              {/* Search Bar */}
              <div className="max-w-md">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
            <input
              type="text"
                    placeholder="Search roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Filter Tabs and Sort */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Quick Filter Tabs - Show most used ones */}
                  <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                    <button
                      onClick={() => setSelectedFilters([])}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        selectedFilters.length === 0
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <span>All Roles</span>
                      <span className="ml-2 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-full">
                        {filterCounts.all}
                      </span>
                    </button>
                    {/* Quick status filters */}
                    {['live', 'live_last_week', 'preview', 'in_process'].map(filterId => {
                      const option = filterOptions.find(opt => opt.id === filterId);
                      return (
                        <button
                          key={filterId}
                          onClick={() => {
                            if (selectedFilters.includes(filterId)) {
                              setSelectedFilters(prev => prev.filter(id => id !== filterId));
                            } else {
                              setSelectedFilters([filterId]);
                            }
                          }}
                          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                            selectedFilters.includes(filterId)
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <span>{option?.label}</span>
                          <span className="ml-2 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-full">
                            {filterCounts[filterId] || 0}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* More Filters Button */}
                  <button
                    onClick={() => setShowFiltersPopup(true)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    More Filters
                    {selectedFilters.some(f => !['live', 'live_last_week', 'preview', 'in_process'].includes(f)) && (
                      <span className="ml-2 w-2 h-2 bg-green-500 rounded-full inline-block"></span>
                    )}
                  </button>
                </div>
          
                <div className="flex items-center space-x-3">
                  <label className="text-sm font-medium text-gray-700">Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="most_apps">Most Applications</option>
                    <option value="least_apps">Least Applications</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Role Cards Grid */}
          {isLoadingRoles ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredAndSortedRoles.length > 0 ? (
                  filteredAndSortedRoles.map(role => {
                    const applicationsCount = parseInt(role["# Apps this month"] || "0");
                    const status = role["Role Drop Status (list visibility)"] || "Unknown";
                    const workingStyle = role["Working Style"] || "Not specified";
                    const compensation = role["Compensation"] || "Not specified";
                    const stream = role["Stream"] || "Not specified";
                    const roleOwner = role["Role Owner"] || "Unassigned";
                    const shortlistData = shortlistActivity[role.id];
                    
                    return (
                  <div 
                    key={role.id} 
                        className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-200"
                      >
                        <div className="space-y-4">
                          {/* Role Header */}
                    <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                              {role["Role Title"] || "Untitled Role"}
                            </h3>
                            <p className="text-gray-600 font-medium">
                              {role["Name (from Startup)"] ? role["Name (from Startup)"][0] : "No company"}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                                Owner: {roleOwner}
                              </span>
                            </div>
                      </div>

                          {/* Status and Applications */}
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                              status === 'Live' 
                                ? 'bg-green-100 text-green-800' 
                                : status === 'Preview'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {status}
                            </span>
                            <span className="text-sm text-gray-500">
                              {applicationsCount} applications
                            </span>
                    </div>

                          {/* Shortlist Activity */}
                          {shortlistData && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                </svg>
                                <span className="text-sm font-medium text-blue-900">Latest Shortlist Activity</span>
                              </div>
                              <p className="text-xs text-blue-700">
                                {shortlistData.candidateCount} candidate{shortlistData.candidateCount !== 1 ? 's' : ''} shortlisted by {shortlistData.addedBy}
                              </p>
                              <p className="text-xs text-blue-600">
                                {(() => {
                                  const date = new Date(shortlistData.addedAt);
                                  const now = new Date();
                                  const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
                                  
                                  if (diffInHours < 1) return 'Less than an hour ago';
                                  if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
                                  const diffInDays = Math.floor(diffInHours / 24);
                                  if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
                                  return date.toLocaleDateString('en-GB');
                                })()}
                              </p>
                            </div>
                          )}

                          {/* Additional Role Data */}
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Working Style:</span>
                              <span className="text-gray-900 font-medium">{workingStyle}</span>
                  </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Compensation:</span>
                              <span className="text-gray-900 font-medium">{compensation}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Stream:</span>
                              <span className="text-gray-900 font-medium">{stream}</span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="space-y-2">
                            <button
                              className="w-full bg-gray-900 hover:bg-black text-white py-3 px-4 rounded-lg font-medium transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                              onClick={() => {
                                fetchRecommendationCriteria(role);
                              }}
                              disabled={isProcessing || isLoadingJDAnalysis}
                            >
                              {isLoadingJDAnalysis ? 'Analyzing Job Description...' : isProcessing ? 'Generating...' : 'Generate Recommendations'}
                            </button>
                            
                            <button
                              className="w-full bg-white hover:bg-gray-50 text-gray-700 py-2 px-4 rounded-lg font-medium transition-colors duration-200 border border-gray-300"
                              onClick={() => window.open(`/role?roleId=${role["URL ID"]}`, '_blank')}
                            >
                              View Role Details
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-full text-center py-12">
                    <div className="text-gray-400 mb-4">
                      <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No roles found</h3>
                    <p className="text-gray-500">
                  {roles.length > 0 
                        ? "No roles match your current filters"
                        : "No roles available"}
                    </p>
                  </div>
                )}
                </div>
              )}
            </div>
          )}
          
        {/* Processing View */}
        {(isProcessing || isLoadingSavedRecommendations) && (
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="mb-8">
              <div className="w-16 h-16 mx-auto mb-6 relative">
                <div className="absolute inset-0 border-4 border-green-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-green-600 rounded-full border-t-transparent animate-spin"></div>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                {isLoadingSavedRecommendations ? 'Loading Saved Recommendations' : 'Generating Recommendations'}
              </h2>
              <p className="text-gray-600 mb-8">
                {isLoadingSavedRecommendations 
                  ? 'Retrieving previously generated recommendations and candidate data...'
                  : 'Analyzing candidates and matching with role requirements...'
                }
              </p>
        </div>

              {Object.entries(activeRecommendations).map(([roleId, info]) => (
              <div key={roleId} className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="font-medium text-gray-900">{info.roleTitle}</span>
                  <span className="text-sm text-gray-600">
                      {info.status === 'processing' 
                        ? `Phase ${info.phase} (${info.progress}%)` 
                        : info.status === 'completed' 
                        ? 'Completed' 
                        : 'Error'}
                    </span>
                  </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${info.progress}%` }}
                  ></div>
                </div>
                  {info.error && (
                  <div className="text-sm text-red-600 mt-2">
                      Error: {info.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
        {/* Results View */}
        {Object.keys(processedResults).length > 0 && !isProcessing && !isLoadingSavedRecommendations && (
          <div className="space-y-6">
            {/* Back Button and Header */}
            <div className="flex items-center justify-between">
                  <button 
                onClick={() => {
                  setProcessedResults({});
                  setSelectedCandidates({});
                  setEditableEmailTemplate('');
                  setActiveTabRoleId(null);
                }}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Roles
                  </button>

              <div className="flex items-center space-x-4">
                {/* Edit & Re-run Button */}
                <button
                  onClick={handleEditAndRerun}
                  className="flex items-center px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg font-medium transition-colors duration-200"
                  disabled={!currentSearchCriteria.role || !currentSearchCriteria.jdAnalysis}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit & Re-run
                </button>
                
                {/* Company Account Button */}
                {(() => {
                  const currentRole = roles.find(r => r.id === Object.keys(processedResults)[0]);
                  const teamLoginLink = currentRole?.["Team Login Link"];
                  
                  return teamLoginLink ? (
                    <a
                      href={teamLoginLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors duration-200"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      Go to Company Account
                    </a>
                  ) : null;
                })()}

                <div className="text-right">
                  <div className="flex items-center justify-end space-x-2 mb-2">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {Object.values(processedResults)[0]?.roleTitle}
                    </h2>
                    {Object.values(processedResults)[0]?.isLoadedFromSaved && (
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        Saved Recommendations
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600">
                    {(() => {
                      const processedData = Object.values(processedResults)[0];
                      const allCandidates = processedData?.candidates?.length || 0;
                      const topCandidates = processedData?.topCandidates?.length || 0;
                      const remainingCandidates = allCandidates - topCandidates;
                      
                      return `${topCandidates} top recommendations, ${remainingCandidates} additional candidates`;
                    })()}
                    {activeTabRoleId && selectedCandidates[activeTabRoleId] && (
                      <span className="ml-2">
                        • {Object.values(selectedCandidates[activeTabRoleId]).filter(Boolean).length} selected
                      </span>
                    )}
                  </p>
                </div>
              </div>
              </div>
              
            {/* Filtering Notification */}
            {!Object.values(processedResults)[0]?.isLoadedFromSaved && (
              <div className="bg-white border border-red rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-black">
                      Filtered Recommendations
                    </h3>
                    <div className="mt-1 text-sm text-black">
                      <p>
                        These recommendations exclude candidates who are:
                      </p>
                      <ul className="mt-1 ml-4 list-disc space-y-1">
                        <li>Currently in the shortlist for this role</li>
                        <li>Previously shortlisted for this role</li>
                        <li>Engaged with this role (have sent applications, been match requested, or are interviewing)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Saved Recommendations Info */}
            {Object.values(processedResults)[0]?.isLoadedFromSaved && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-900">
                      Previously Generated Recommendations
                    </h3>
                    <div className="mt-1 text-sm text-blue-800">
                      <p>
                        These are previously generated AI recommendations that were saved to Airtable. 
                        You can still select candidates, save to shortlist, and generate communication templates.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
              
            {/* Selection Summary & Action Buttons */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Selection Progress
                      </p>
                      <p className="text-xs text-gray-600">
                        {(() => {
                          const roleId = Object.keys(processedResults)[0];
                          const selectedCount = Object.values(selectedCandidates[roleId] || {}).filter(Boolean).length;
                          const totalCandidates = Object.values(processedResults)[0]?.candidates?.length || 0;
                          return `${selectedCount} of ${totalCandidates} candidates selected`;
                        })()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="h-8 w-px bg-gray-300"></div>
                  
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Next Step
                      </p>
                      <p className="text-xs text-gray-600">
                        Save to shortlist or generate communication templates
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleCreateTemplate}
                    className="px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
                    disabled={!selectedCandidates[Object.keys(processedResults)[0]] || Object.values(selectedCandidates[Object.keys(processedResults)[0]] || {}).filter(Boolean).length === 0}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Create Template</span>
                  </button>
                  
                  <button
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
                    onClick={() => saveToShortlist(Object.keys(processedResults)[0])}
                    disabled={isConfirming || !selectedCandidates[Object.keys(processedResults)[0]] || Object.values(selectedCandidates[Object.keys(processedResults)[0]] || {}).filter(Boolean).length === 0}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    <span>{isConfirming ? 'Saving...' : 'Save to Shortlist'}</span>
                  </button>
                </div>
              </div>
            </div>
                  
            {/* Candidates Section - Full Width Priority */}
            <div className="space-y-6">
              {/* Candidates */}
              <div className="space-y-6">
                {/* Top Recommended Candidates */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-8 bg-green-500 rounded-full"></div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          Top Recommended Candidates
                        </h3>
                        <p className="text-sm text-gray-600">
                          {Object.values(processedResults)[0]?.topCandidates?.length || 0} AI-selected best matches
                        </p>
                      </div>
                    </div>
                    
                    {/* Match Quality Legend */}
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500 font-medium">Match Quality:</span>
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full font-medium text-xs">80%+ Excellent</span>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full font-medium text-xs">60-79% Good</span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full font-medium text-xs">&lt;60% Fair</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[50rem] overflow-y-auto">
                    {(() => {
                      const topCands = Object.values(processedResults)[0]?.topCandidates || [];
                      const roleId = Object.keys(processedResults)[0];
                      return topCands.length > 0 ? (
                        topCands.map((candidate, index) => {
                          const isSelected = selectedCandidates[roleId]?.[candidate.id] || false;
                          
                          return (
                            <div 
                              key={candidate.id || index}
                              className="relative"
                            >
                              {/* Selection Button Overlay */}
                              <div className="absolute top-2 left-2 z-30">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCandidateSelection(roleId, candidate.id);
                                  }}
                                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center space-x-2 shadow-lg ${
                                    isSelected 
                                      ? 'bg-green-600 text-white hover:bg-green-700' 
                                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                  }`}
                                >
                                  {isSelected ? (
                                    <>
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                      <span>Selected</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                      </svg>
                                      <span>Select</span>
                                    </>
                                  )}
                                </button>
                              </div>

                              {/* Rank Badge */}
                              <div className="absolute top-2 right-2 z-30 bg-gray-900 bg-opacity-90 text-white rounded-full px-2 py-1 shadow-lg">
                                <span className="text-xs font-bold">#{index + 1}</span>
                              </div>

                              {/* Full GeneralistCard Component */}
                              <GeneralistCard
                                candidate={candidate}
                                recommendationData={{
                                  score: candidate.score,
                                  reason: candidate.reason,
                                  filtered: candidate.filtered || false
                                }}
                                showRecommendationInfo={true}
                                showInternalFields={true}
                                displayShortlistButton={false}
                                roles={[]} 
                                candidatesStatus={[]}
                                shortlistedCandidates={[]}
                                handleSelectCandidate={() => {}}
                              />
                            </div>
                          );
                        })
                      ) : (
                        <div className="col-span-full text-center py-12">
                          <div className="text-gray-400 mb-4">
                            <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <p className="text-gray-500">No top candidates found</p>
                        </div>
                      );
                        })()}
                  </div>
                </div>

              {/* Remaining Candidates Section */}
              {(() => {
                const processedData = Object.values(processedResults)[0];
                const allCandidates = processedData?.candidates || [];
                const topCandidates = processedData?.topCandidates || [];
                
                const topCandidateIds = new Set(topCandidates.map(c => c.id));
                const remainingCandidates = allCandidates.filter(c => !topCandidateIds.has(c.id));
                
                if (remainingCandidates.length === 0) return null;
                
                return (
                  <div className="bg-white rounded-xl border border-gray-200">
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-4">
                        <button
                          onClick={() => setShowRemainingCandidates(!showRemainingCandidates)}
                          className="flex items-center space-x-3 text-lg font-semibold text-gray-900 hover:text-gray-700 transition-colors group"
                        >
                          <div className="w-2 h-6 bg-gray-400 rounded-full group-hover:bg-gray-500 transition-colors"></div>
                          <div>
                            <span>Additional Candidates ({remainingCandidates.length})</span>
                            <p className="text-sm text-gray-600 font-normal">
                              Other analyzed candidates that didn't make the top recommendations
                            </p>
                          </div>
                          <svg 
                            className={`w-5 h-5 text-gray-400 transition-transform ${showRemainingCandidates ? 'rotate-180' : ''}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                      
                      {showRemainingCandidates && (
                        <div className="border-t border-gray-100 pt-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[50rem] overflow-y-auto bg-gray-50 rounded-lg p-4">
                            {remainingCandidates.map((candidate, index) => {
                              const isSelected = selectedCandidates[Object.keys(processedResults)[0]]?.[candidate.id] || false;
                              const roleId = Object.keys(processedResults)[0];
                              
                              return (
                                <div 
                                  key={candidate.id || index}
                                  className="relative"
                                >
                                  {/* Selection Button Overlay */}
                                  <div className="absolute top-2 left-2 z-30">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleCandidateSelection(roleId, candidate.id);
                                      }}
                                      className={`px-2 py-1 text-xs font-medium rounded-md transition-all duration-200 flex items-center space-x-1.5 shadow-lg ${
                                        isSelected 
                                          ? 'bg-green-600 text-white hover:bg-green-700' 
                                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                      }`}
                                    >
                                      {isSelected ? (
                                        <>
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                          <span>Selected</span>
                                        </>
                                      ) : (
                                        <>
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                          </svg>
                                          <span>Select</span>
                                        </>
                                      )}
                                    </button>
                                  </div>
                                  
                                  {/* Rank Badge */}
                                  <div className="absolute top-2 right-2 z-30 bg-gray-900 bg-opacity-90 text-white rounded-full px-1.5 py-0.5 shadow-lg">
                                    <span className="text-xs font-bold">#{topCandidates.length + index + 1}</span>
                                  </div>
                                  
                                  {/* Full GeneralistCard Component */}
                                  <GeneralistCard
                                    candidate={candidate}
                                    recommendationData={{
                                      score: candidate.score,
                                      reason: candidate.reason,
                                      filtered: candidate.filtered || false
                                    }}
                                    showRecommendationInfo={true}
                                    showInternalFields={true}
                                    displayShortlistButton={false}
                                    roles={[]} 
                                    candidatesStatus={[]}
                                    shortlistedCandidates={[]}
                                    handleSelectCandidate={() => {}} 
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Communication Templates - Collapsible Section */}
            <div className="mt-8" ref={templatesRef}>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                {/* Collapsed Header */}
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                  onClick={() => setShowTemplates(!showTemplates)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-3">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <h3 className="text-lg font-semibold text-gray-900">Communication Templates</h3>
                        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                          {activeTabRoleId && selectedCandidates[activeTabRoleId] 
                            ? `${Object.values(selectedCandidates[activeTabRoleId]).filter(Boolean).length} candidates selected`
                            : 'No candidates selected'
                          }
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {!showTemplates && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyTemplateToClipboard();
                            }}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
                            disabled={!activeTabRoleId || !selectedCandidates[activeTabRoleId] || Object.values(selectedCandidates[activeTabRoleId] || {}).filter(Boolean).length === 0}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>Copy {activeTemplate === 'email' ? 'Email' : 'WhatsApp'}</span>
                          </button>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">
                          {showTemplates ? 'Collapse' : 'Expand'}
                        </span>
                        <svg 
                          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${showTemplates ? 'rotate-180' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {!showTemplates && (
                    <div className="mt-3 text-sm text-gray-600">
                      Click to expand and customize your {activeTemplate === 'email' ? 'email' : 'WhatsApp'} template • Auto-synced with candidate selection
                    </div>
                  )}
                </div>

                {/* Expanded Content */}
                {showTemplates && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    <div className="pt-6">
                      <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center space-x-4">
                          {/* Template Type Toggle */}
                          <div className="flex bg-gray-100 rounded-lg p-1">
                            <button
                              onClick={() => setActiveTemplate('email')}
                              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center space-x-2 ${
                                activeTemplate === 'email'
                                  ? 'bg-white text-gray-900 shadow-sm'
                                  : 'text-gray-600 hover:text-gray-900'
                              }`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <span>Email</span>
                            </button>
                            <button
                              onClick={() => setActiveTemplate('whatsapp')}
                              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center space-x-2 ${
                                activeTemplate === 'whatsapp'
                                  ? 'bg-white text-gray-900 shadow-sm'
                                  : 'text-gray-600 hover:text-gray-900'
                              }`}
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.566z"/>
                              </svg>
                              <span>WhatsApp</span>
                            </button>
                          </div>
                        </div>
                        
                        <div className="flex space-x-2">
                          {/* Show view mode toggle only for email */}
                          {activeTemplate === 'email' && (
                          <div className="flex bg-gray-100 rounded-lg p-1">
                            <button
                              onClick={() => setEmailViewMode('preview')}
                              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                emailViewMode === 'preview'
                                  ? 'bg-white text-gray-900 shadow-sm'
                                  : 'text-gray-600 hover:text-gray-900'
                              }`}
                            >
                              Preview
                            </button>
                            <button
                              onClick={() => setEmailViewMode('source')}
                              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                emailViewMode === 'source'
                                  ? 'bg-white text-gray-900 shadow-sm'
                                  : 'text-gray-600 hover:text-gray-900'
                              }`}
                            >
                              HTML Source
                            </button>
                          </div>
                          )}
                          <button
                            onClick={copyTemplateToClipboard}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>Copy</span>
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-4">Auto-synced with your candidate selection</p>
                      
                      {activeTemplate === 'email' ? (
                        emailViewMode === 'preview' ? (
                        <div 
                          className="w-full h-96 p-4 text-sm leading-relaxed border border-gray-200 rounded-lg bg-gray-50 overflow-y-auto"
                          dangerouslySetInnerHTML={{ __html: editableEmailTemplate.replace(/\n/g, '<br>') }}
                        />
                      ) : (
                        <textarea
                          value={editableEmailTemplate}
                          onChange={(e) => setEditableEmailTemplate(e.target.value)}
                          className="w-full h-96 p-4 text-sm font-mono leading-relaxed resize-none border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Email template will appear here..."
                        />
                        )
                      ) : (
                        // WhatsApp template (always plain text)
                        <div className="relative">
                          <div className="absolute top-3 right-3 z-10">
                            <div className="flex items-center space-x-2 bg-green-100 px-3 py-1 rounded-full text-xs font-medium text-green-800">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.566z"/>
                              </svg>
                              <span>WhatsApp Ready</span>
                            </div>
                          </div>
                          <textarea
                            value={editableEmailTemplate}
                            onChange={(e) => setEditableEmailTemplate(e.target.value)}
                            className="w-full h-96 p-4 text-sm leading-relaxed resize-none border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-green-50"
                            placeholder="WhatsApp template will appear here..."
                            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
          )}
      </div>

      {/* Filters Popup Modal */}
      {showFiltersPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Filter Roles</h3>
              <button
                onClick={() => setShowFiltersPopup(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Role Status Filters */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Role Status
                  </h4>
                  <div className="space-y-3">
                    {filterOptions.filter(option => !option.id.startsWith('owner_')).map(option => (
                      <label key={option.id} className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedFilters.includes(option.id)}
                          onChange={() => toggleFilter(option.id)}
                          className="mt-1 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900 text-sm">{option.label}</span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                              {filterCounts[option.id] || 0}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Team Member Filters */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Role Owner
                  </h4>
                  <div className="space-y-3">
                    {/* All Owners option */}
                    <label className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="roleOwner"
                        checked={!selectedFilters.some(f => f.startsWith('owner_'))}
                        onChange={() => {
                          // Clear all owner filters
                          setSelectedFilters(prev => prev.filter(id => !id.startsWith('owner_')));
                        }}
                        className="mt-1 h-4 w-4 text-green-600 border-gray-300 focus:ring-green-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900 text-sm">All Owners</span>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                            {filterCounts.all}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Show roles from all team members</div>
                      </div>
                    </label>
                    
                    {/* Individual owner options */}
                    {filterOptions.filter(option => option.id.startsWith('owner_')).map(option => (
                      <label key={option.id} className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                        <input
                          type="radio"
                          name="roleOwner"
                          checked={selectedFilters.includes(option.id)}
                          onChange={() => toggleFilter(option.id)}
                          className="mt-1 h-4 w-4 text-green-600 border-gray-300 focus:ring-green-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900 text-sm">{option.label}</span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                              {filterCounts[option.id] || 0}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex justify-between items-center">
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Clear all filters
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowFiltersPopup(false)}
                    className="px-4 py-2 text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 rounded-md font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={applyFilters}
                    className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors duration-200 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    View Results ({filteredAndSortedRoles.length})
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TagReviewModal */}
      <TagReviewModal
        open={showTagReviewModal}
        onClose={handleTagReviewClose}
        onProceed={handleTagReviewProceed}
        jdAnalysis={currentJDAnalysis}
        roleTitle={selectedRoleForReview?.["Role Title"] || "Role"}
        isEditMode={currentSearchCriteria.role !== null} // Edit mode when we have previous search criteria
        jobDescriptionUrl={selectedRoleForReview?.["Job Description PDF"] || null}
        amNote={selectedRoleForReview?.["Account Manager comment on the role"]}
        showGenderFilter={true}
        roleData={selectedRoleForReview}
        showStreamFilter={true}
        defaultGender={currentSearchCriteria.gender}
        defaultSelectedStream={currentSearchCriteria.selectedStream}
        defaultRequireNoVisa={currentSearchCriteria.requireNoVisa}
      />
    </div>
  );
} 